import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/cn';

/**
 * `saving` (U1): give a card that saves its own draft its busy flag. While it is true nothing in the card can be changed —
 * what is typed between Save and the re-read would be put back to the saved values without a word. The wrapper takes no
 * box of its own, so the card lays out as before.
 */
export function Card({ className, saving, children, ...rest }: HTMLAttributes<HTMLDivElement> & { saving?: boolean }) {
  return (
    <div className={cn('rounded-card bg-surface elevate', className)} {...rest}>
      {saving === undefined ? children : <fieldset disabled={saving} aria-busy={saving} className="contents" data-saving={saving || undefined}>{children}</fieldset>}
    </div>
  );
}

/**
 * `info` is an optional help icon shown beside the title; a header without one is unchanged. Give the icon
 * `className="static"` so its tooltip opens from the title's left edge and stays on a phone screen.
 */
export function CardHeader({ title, description, actions, className, info }: { title: ReactNode; description?: ReactNode; actions?: ReactNode; className?: string; info?: ReactNode }) {
  return (
    <div className={cn('flex flex-wrap items-start justify-between gap-3 border-b border-line px-5 py-4', className)}>
      <div className="min-w-0">
        {info ? (
          <div className="relative flex items-center gap-1">
            <h2 className="text-[15px] font-semibold text-ink">{title}</h2>
            {info}
          </div>
        ) : <h2 className="text-[15px] font-semibold text-ink">{title}</h2>}
        {description && <p className="mt-0.5 text-[13px] text-muted">{description}</p>}
      </div>
      {actions && <div className="flex min-w-0 max-w-full flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Stat({ label, value, hint, tone }: { label: string; value: ReactNode; hint?: ReactNode; tone?: 'warning' | 'danger' | 'success' }) {
  return (
    <div className="min-w-0">
      <div className="text-[12px] font-medium uppercase tracking-wide text-muted">{label}</div>
      <div className={cn('mt-1 text-2xl font-semibold tabular-nums text-ink', tone === 'warning' && 'text-st-warning', tone === 'danger' && 'text-st-danger', tone === 'success' && 'text-st-success')}>
        {value}
      </div>
      {hint && <div className="mt-0.5 text-[13px] text-muted">{hint}</div>}
    </div>
  );
}

export function PageHeader({ title, description, actions, eyebrow }: { title: ReactNode; description?: ReactNode; actions?: ReactNode; eyebrow?: ReactNode }) {
  return (
    <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div className="min-w-0">
        {eyebrow && <div className="mb-1 text-[13px] font-medium text-muted">{eyebrow}</div>}
        <h1 tabIndex={-1} data-route-heading className="text-[22px] font-semibold tracking-tight text-ink outline-none sm:text-2xl">{title}</h1>
        {description && <p className="mt-1 max-w-prose break-words text-sm text-ink-2">{description}</p>}
      </div>
      {actions && <div className="flex min-w-0 max-w-full flex-wrap items-center gap-2">{actions}</div>}
    </header>
  );
}
